import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	SignatureCanvas,
	type SignatureCanvasHandle,
} from "../../components/tasks/SignatureCanvas";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import { signConsent } from "../../services/consentService";
import { getGetReadyTasks } from "../../services/taskService";
import styles from "./SignConsentPage.module.css";

interface SignedConsentMeta {
	signedAt: string;
	signatureUrl: string;
	signerName: string | null;
}

function readSignedConsentMeta(meta: unknown): SignedConsentMeta | null {
	if (typeof meta !== "object" || meta === null) return null;
	const value = (meta as { signedConsent?: unknown }).signedConsent;
	if (typeof value !== "object" || value === null) return null;
	const v = value as Record<string, unknown>;
	if (typeof v.signedAt !== "string" || typeof v.signatureUrl !== "string") {
		return null;
	}
	return {
		signedAt: v.signedAt,
		signatureUrl: v.signatureUrl,
		signerName: typeof v.signerName === "string" ? v.signerName : null,
	};
}

const ZOOM_LEVELS = [75, 100, 125, 150] as const;
type ZoomLevel = (typeof ZOOM_LEVELS)[number];

const ZOOM_CLASS_BY_LEVEL: Record<ZoomLevel, string> = {
	75: styles.documentPageZoom75,
	100: styles.documentPageZoom100,
	125: styles.documentPageZoom125,
	150: styles.documentPageZoom150,
};

const CONSENT_TASK_KEY = "sign_consent";
const CONSENT_TYPE = "treatment_initial";
const CONSENT_VERSION = "v1";

export function SignConsentPage() {
	const { t } = useClientTranslations("getReady");
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { logEvent } = useAnalytics();
	const signatureRef = useRef<SignatureCanvasHandle>(null);
	const [hasSignature, setHasSignature] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(100);

	const { data: tasksResponse } = useQuery({
		queryKey: ["tasks"],
		queryFn: getGetReadyTasks,
	});

	const consentTask = useMemo(() => {
		if (!tasksResponse) return null;
		for (const step of tasksResponse.steps) {
			for (const task of step.tasks) {
				if (task.kind === "catalog" && task.taskKey === CONSENT_TASK_KEY) {
					return task;
				}
			}
		}
		return null;
	}, [tasksResponse]);

	const signedConsent = useMemo<SignedConsentMeta | null>(() => {
		if (!consentTask) return null;
		return readSignedConsentMeta(consentTask.meta);
	}, [consentTask]);

	const signedDateLabel = useMemo(() => {
		if (!signedConsent) return null;
		const date = new Date(signedConsent.signedAt);
		if (Number.isNaN(date.getTime())) return signedConsent.signedAt;
		return date.toLocaleDateString(undefined, {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	}, [signedConsent]);

	const zoomIndex = useMemo(() => ZOOM_LEVELS.indexOf(zoomLevel), [zoomLevel]);
	const canZoomOut = zoomIndex > 0;
	const canZoomIn = zoomIndex < ZOOM_LEVELS.length - 1;
	const zoomClass = ZOOM_CLASS_BY_LEVEL[zoomLevel];
	const zoomLabel = `${zoomLevel}%`;

	const p = useCallback(
		(key: string) => t(`taskPages.signConsent.${key}`),
		[t],
	);

	const handleClose = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	const handleCancel = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	const handleZoomOut = useCallback(() => {
		setZoomLevel((current) => {
			const currentIndex = ZOOM_LEVELS.indexOf(current);
			return ZOOM_LEVELS[Math.max(0, currentIndex - 1)];
		});
	}, []);

	const handleZoomIn = useCallback(() => {
		setZoomLevel((current) => {
			const currentIndex = ZOOM_LEVELS.indexOf(current);
			return ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, currentIndex + 1)];
		});
	}, []);

	const handleZoomReset = useCallback(() => {
		setZoomLevel(100);
	}, []);

	const handleSign = useCallback(async () => {
		try {
			logEvent("get_ready_sign_consent_pressed", undefined, {
				has_signature: String(hasSignature),
			});
		} catch {
			// non-blocking
		}

		const signatureDataUrl = signatureRef.current?.getDataUrl() ?? null;
		if (!signatureDataUrl) {
			toast.error(p("signer.error"));
			return;
		}

		setIsSubmitting(true);
		try {
			await signConsent({
				taskId: consentTask?.id,
				taskKey: CONSENT_TASK_KEY,
				consentType: CONSENT_TYPE,
				consentVersion: CONSENT_VERSION,
				signatureDataUrl,
			});
			await queryClient.invalidateQueries({ queryKey: ["tasks"] });
			try {
				toast.success(p("signer.success"));
			} catch {
				// non-blocking
			}
			navigate("/get-ready");
		} catch (error) {
			console.error("[SignConsentPage] failed to sign consent", error);
			try {
				toast.error(p("signer.error"));
			} catch {
				// non-blocking
			}
		} finally {
			setIsSubmitting(false);
		}
	}, [consentTask?.id, hasSignature, logEvent, navigate, p, queryClient]);

	const signDisabled = !hasSignature || isSubmitting;

	return (
		<div className={styles.page}>
			{/* Top bar */}
			<div className={styles.topBar}>
				<span className={styles.fileName}>{p("fileName")}</span>
				<button
					type="button"
					className={styles.closeButton}
					onClick={handleClose}
					aria-label={p("close")}
				>
					<X className={styles.closeIcon} aria-hidden="true" />
				</button>
			</div>

			{/* Viewer toolbar */}
			<div className={styles.viewerToolbar}>
				<button
					type="button"
					className={styles.zoomButton}
					onClick={handleZoomOut}
					disabled={!canZoomOut}
					aria-label={p("toolbar.zoomOut")}
				>
					<span className={styles.zoomGlyph} aria-hidden="true">
						−
					</span>
				</button>
				<span className={styles.zoomLabel}>{zoomLabel}</span>
				<button
					type="button"
					className={styles.zoomButton}
					onClick={handleZoomIn}
					disabled={!canZoomIn}
					aria-label={p("toolbar.zoomIn")}
				>
					<span className={styles.zoomGlyph} aria-hidden="true">
						+
					</span>
				</button>
				<button
					type="button"
					className={styles.resetButton}
					onClick={handleZoomReset}
				>
					{p("toolbar.reset")}
				</button>
			</div>

			{/* Document area */}
			<div className={styles.documentArea}>
				<div className={`${styles.documentPage} ${zoomClass}`}>
					<p className={styles.consentFormTitle}>{p("document.title")}</p>
					<h2 className={styles.consentFormHeading}>{p("document.heading")}</h2>
					<p className={styles.consentFormParagraph}>
						{p("document.paragraphs.consent")}
					</p>
					<p className={styles.consentFormParagraph}>
						{p("document.paragraphs.risks")}
					</p>
					<div className={styles.consentFormSection}>
						<p className={styles.consentFormSectionHeading}>
							{p("document.understanding.title")}
						</p>
						<ul className={styles.consentFormList}>
							<li className={styles.consentFormListItem}>
								{p("document.understanding.items.success")}
							</li>
							<li className={styles.consentFormListItem}>
								{p("document.understanding.items.multiples")}
							</li>
							<li className={styles.consentFormListItem}>
								{p("document.understanding.items.risks")}
							</li>
							<li className={styles.consentFormListItem}>
								{p("document.understanding.items.discontinue")}
							</li>
						</ul>
					</div>
					<p className={styles.consentFormParagraph}>
						{p("document.paragraphs.authorization")}
					</p>
					<hr className={styles.consentFormDivider} />
					<p className={styles.consentFormSectionHeading}>
						{p("document.patient.title")}
					</p>
					<div className={styles.consentFormPatientInfo}>
						<div className={styles.consentFormPatientField}>
							<span className={styles.consentFormPatientLabel}>
								{p("document.patient.fullName")}
							</span>
							<span className={styles.consentFormPatientValue}>
								{p("document.patient.fullNameValue")}
							</span>
						</div>
						<div className={styles.consentFormPatientField}>
							<span className={styles.consentFormPatientLabel}>
								{p("document.patient.dateOfBirth")}
							</span>
							<span className={styles.consentFormPatientValue}>
								{p("document.patient.dateOfBirthValue")}
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Signer panel */}
			<div className={styles.signerPanel}>
				{signedConsent ? (
					<>
						<div className={styles.signerHeader}>
							<span className={styles.signerLabel}>{p("signed.label")}</span>
						</div>
						<div className={styles.signedPreview}>
							<img
								src={signedConsent.signatureUrl}
								alt={p("signed.imageAlt")}
								className={styles.signedImage}
							/>
							<div className={styles.signedMeta}>
								{signedDateLabel && (
									<span className={styles.signedMetaPrimary}>
										{t("taskPages.signConsent.signed.signedOn", {
											date: signedDateLabel,
										})}
									</span>
								)}
								{signedConsent.signerName && (
									<span className={styles.signedMetaSecondary}>
										{t("taskPages.signConsent.signed.signedBy", {
											name: signedConsent.signerName,
										})}
									</span>
								)}
							</div>
						</div>
						<button
							type="button"
							className={styles.signedCloseButton}
							onClick={handleClose}
						>
							{p("signed.close")}
						</button>
					</>
				) : (
					<>
						<div className={styles.signerHeader}>
							<span className={styles.signerLabel}>{p("signer.label")}</span>
							<span className={styles.signerChevron} aria-hidden="true" />
						</div>
						<SignatureCanvas
							ref={signatureRef}
							placeholderText={p("signer.placeholder")}
							clearLabel={p("signer.clear")}
							onChange={setHasSignature}
						/>
						<div className={styles.signerButtons}>
							<button
								type="button"
								className={styles.signButton}
								onClick={handleSign}
								disabled={signDisabled}
							>
								{isSubmitting ? p("signer.signing") : p("signer.sign")}
							</button>
							<button
								type="button"
								className={styles.cancelButton}
								onClick={handleCancel}
								disabled={isSubmitting}
							>
								{p("signer.cancel")}
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
